import { useEffect, useRef, useState } from "react";
import { isAndroid, isMobile } from "react-device-detect";
import { useParams } from "react-router";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { getIbanQr } from "store/features/publicIbanQrSlice";
import {
  createIbanInvoicePayment,
  setIsPaymentUrlModalOpen,
} from "store/features/ibanInvoicePaymentSlice";

import { AmountTypeEnum, IbanInvoiceResponseDto } from "api";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

import { Bank, IbanKeys } from "shared/constants/banks";
import {
  constructBankUrl,
  constructPaymentUrl,
} from "shared/functions/ibanQrHelpers";
import { FormValues } from "components/IbanInvoiceInfo/IbanPaymentForm/validation";

type Params = {
  uid: string;
};

export const useIbanQr = () => {
  const dispatch = useAppDispatch();
  const { uid } = useParams<Params>();

  const paymentUrl = useAppSelector(
    (state) => state.ibanInvoicePayment.paymentUrl,
  );

  const defaultErrorText =
    "Будь ласка, зв’яжіться з особою, яка поділилася ним, щоб отримати нове.";

  const currentBankRef = useRef<Bank | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [iban, setIban] = useState<IbanInvoiceResponseDto | null>(null);
  const [isIbanLoading, setIsIbanLoading] = useState<boolean>(true);

  const [errorText, setErrorText] = useState<string>("");
  const [showModal, setShowModal] = useState(false);

  const [isBankLoading, setIsBankLoading] = useState<boolean>(false);
  const [preferredBank, setPreferredBank] = useState(
    localStorage.getItem(IbanKeys.PREFERRED_BANK) || "",
  );

  useEffect(() => {
    (async () => {
      setIsIbanLoading(true);
      if (uid) {
        await dispatch(
          getIbanQr({
            uid,
            onSuccess: (iban: IbanInvoiceResponseDto) => {
              setIban(iban);
              setIsIbanLoading(false);
            },
            onError: (errorText: string) => {
              setErrorText(errorText || defaultErrorText);
              setIsIbanLoading(false);
            },
          }),
        );
      }
    })();
  }, [uid, dispatch]);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleVisibilityChange = () => {
    if (document.hidden) {
      setIsBankLoading((prev) => {
        if (prev && currentBankRef.current) {
          setPreferredBank(currentBankRef.current.name);
          localStorage.setItem(
            IbanKeys.PREFERRED_BANK,
            currentBankRef.current.name,
          );
          analytics().sendConversionEvent(
            `${AnalyticsEvents.iban_qr_pay_}${currentBankRef.current.shortName}`,
            null,
            true,
          );

          currentBankRef.current = null;
        }

        if (prev) {
          timeoutRef.current && clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
          return false;
        }

        return prev;
      });
    }
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  const handlePayment = async (
    formValues?: FormValues,
    bank?: Bank,
  ): Promise<void> => {
    if (!iban) return;

    const isPayerDefined = iban.amountType === AmountTypeEnum.PayerDefined;
    const { requestPayerFullName, requestOrderNumber } =
      iban.paymentPurposeOptions || {};

    if (
      (isPayerDefined || requestPayerFullName || requestOrderNumber) &&
      !formValues
    ) {
      return;
    }

    try {
      bank && setIsBankLoading(true);
      const data = await dispatch(
        createIbanInvoicePayment({
          body: {
            ibanInvoiceUid: iban.ibanQrInvoiceUid,
            ...(isPayerDefined && { amount: formValues?.amount }),
            ...(requestPayerFullName && {
              payerFullName: formValues?.payerFullName,
            }),
            ...(requestOrderNumber && { orderNumber: formValues?.orderNumber }),
          },
        }),
      ).unwrap();

      const url = data?.ibanPaymentUrl;

      if (!url) return;

      if (isMobile) {
        bank ? onRedirectBank(url, bank) : onRedirectDefault(url);
      } else {
        dispatch(setIsPaymentUrlModalOpen(true));
      }
    } catch (error) {
      bank && setIsBankLoading(false);
    }
  };

  const onRedirectDefault = (url: string) => {
    currentBankRef.current = null;
    analytics().sendConversionEvent(
      AnalyticsEvents.iban_qr_pay_default,
      null,
      true,
    );
    const paymentUrl = constructPaymentUrl(url);

    if (paymentUrl) {
      window.location.href = paymentUrl;
    }

    if (isAndroid) {
      setIsBankLoading(true);
      timeoutRef.current = setTimeout(() => {
        analytics().sendConversionEvent(
          AnalyticsEvents.iban_qr_pay_modal_open,
          null,
          true,
        );
        setShowModal(true);
        setIsBankLoading(false);
      }, 5000);
    }
  };

  const onRedirectBank = (url: string, bank: Bank) => {
    analytics().sendConversionEvent(
      AnalyticsEvents.iban_qr_pay_with_bank,
      null,
      true,
    );

    currentBankRef.current = bank;
    const paymentUrl = constructBankUrl(bank, url);

    setIsBankLoading(true);
    if (paymentUrl) {
      window.location.assign(paymentUrl);
    }

    timeoutRef.current = setTimeout(() => {
      setIsBankLoading((prevIsLoading) => {
        if (prevIsLoading) {
          analytics().sendConversionEvent(
            AnalyticsEvents.iban_qr_pay_modal_open,
            null,
            true,
          );
          setShowModal(true);
          return false;
        }

        return prevIsLoading;
      });
    }, 5000);
  };

  return {
    iban,
    isIbanLoading,
    errorText,
    showModal,
    isBankLoading,
    preferredBank,
    paymentUrl,
    currentBankRef,
    onCloseModal,
    handlePayment,
  };
};
